import { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoInspectionsQuery,
  useCreatePtoInspectionMutation,
  useSetPtoInspectionResultMutation,
  useListPtoIdPackagesQuery,
  useCreatePtoIdPackageMutation,
  useGetPtoIdPackageQuery,
  useSetPtoIdPackageStatusMutation,
  useListPtoExecutiveActsQuery,
  type PtoInspection,
  type PtoIdPackage,
  type PtoExecutiveAct,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
`;

const ItemRow = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const AckOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 4px 0;
`;

const INSPECTION_KINDS = [
  { value: 'acceptance', label: 'Приёмочная' },
  { value: 'hidden_works', label: 'Скрытые работы' },
  { value: 'incoming', label: 'Входной контроль' },
  { value: 'other', label: 'Прочее' },
];

function inspKindLabel(k: string): string {
  return INSPECTION_KINDS.find((x) => x.value === k)?.label ?? k;
}
function inspStatusLabel(s: string): string {
  return s === 'passed' ? 'Пройдена' : s === 'failed' ? 'Не пройдена' : 'Запланирована';
}
function pkgStatusLabel(s: string): string {
  switch (s) {
    case 'submitted':
      return 'Сдан в СТДО';
    case 'accepted':
      return 'Принят';
    case 'rejected':
      return 'Отклонён';
    default:
      return 'Комплектуется';
  }
}

export function PtoInspections() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');

  const { data: inspData } = useListPtoInspectionsQuery(projectId, { skip: !projectId });
  const [createInsp, { isLoading: savingInsp }] = useCreatePtoInspectionMutation();
  const [setResult] = useSetPtoInspectionResultMutation();
  const inspections: PtoInspection[] = inspData?.items ?? [];

  const { data: pkgData } = useListPtoIdPackagesQuery(projectId, { skip: !projectId });
  const [createPkg, { isLoading: savingPkg }] = useCreatePtoIdPackageMutation();
  const [setPkgStatus] = useSetPtoIdPackageStatusMutation();
  const packages: PtoIdPackage[] = pkgData?.items ?? [];

  const { data: actsData } = useListPtoExecutiveActsQuery({ projectId }, { skip: !projectId });
  const acts: PtoExecutiveAct[] = actsData?.items ?? [];

  const [iKind, setIKind] = useState('acceptance');
  const [iTitle, setITitle] = useState('');
  const [iDate, setIDate] = useState('');

  const [pTitle, setPTitle] = useState('');
  const [pActs, setPActs] = useState<Record<string, boolean>>({});
  const [selectedPkg, setSelectedPkg] = useState<string | null>(null);
  const [stdoRef, setStdoRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: detailData } = useGetPtoIdPackageQuery(selectedPkg ?? '', { skip: !selectedPkg });
  const detail = detailData?.detail;

  async function onCreateInsp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!iTitle.trim()) {
      setError('Укажите наименование инспекции.');
      return;
    }
    try {
      await createInsp({
        projectId,
        kind: iKind,
        title: iTitle.trim(),
        scheduled_date: iDate.trim() || undefined,
      }).unwrap();
      setITitle('');
      setIDate('');
    } catch {
      setError('Не удалось создать инспекцию.');
    }
  }

  async function onCreatePkg(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const act_ids = Object.entries(pActs)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!pTitle.trim()) {
      setError('Укажите наименование комплекта.');
      return;
    }
    try {
      const res = await createPkg({ projectId, title: pTitle.trim(), act_ids }).unwrap();
      setPTitle('');
      setPActs({});
      setSelectedPkg(res.package.id);
    } catch {
      setError('Не удалось создать комплект.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Приёмочные инспекции и сдача ИД</h1>
          <Muted>Календарь инспекций · комплекты исполнительной документации (архив/СТДО)</Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      <Columns>
        {/* Инспекции */}
        <div>
          <h3 style={{ marginTop: 0 }}>Приёмочные инспекции</h3>
          {can_write && (
            <Panel as="form" onSubmit={onCreateInsp}>
              <Field>
                Вид
                <Select value={iKind} onChange={(e) => setIKind(e.target.value)}>
                  {INSPECTION_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                Наименование
                <Input
                  value={iTitle}
                  onChange={(e) => setITitle(e.target.value)}
                  placeholder="Освидетельствование скрытых работ — ростверк"
                />
              </Field>
              <Field>
                Дата (опц., ГГГГ-ММ-ДД)
                <Input value={iDate} onChange={(e) => setIDate(e.target.value)} placeholder="2026-07-01" />
              </Field>
              <Button $variant="primary" type="submit" disabled={savingInsp || !iTitle.trim()}>
                {savingInsp ? 'Создание…' : 'Запланировать'}
              </Button>
            </Panel>
          )}
          {inspections.length === 0 ? (
            <Muted>Инспекций пока нет.</Muted>
          ) : (
            <Panel>
              {inspections.map((i) => (
                <ItemRow key={i.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{i.title}</span>
                    <Muted style={{ fontSize: 12 }}>{inspStatusLabel(i.status)}</Muted>
                  </div>
                  <Muted style={{ fontSize: 12, marginTop: 2 }}>
                    {inspKindLabel(i.kind)}
                    {i.scheduled_date ? ` · ${i.scheduled_date}` : ''}
                  </Muted>
                  {i.result_note && (
                    <Muted style={{ fontSize: 12, marginTop: 2 }}>Результат: {i.result_note}</Muted>
                  )}
                  {can_write && i.status === 'planned' && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => setResult({ id: i.id, status: 'passed' })}
                      >
                        Пройдена
                      </Button>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => setResult({ id: i.id, status: 'failed' })}
                      >
                        Не пройдена
                      </Button>
                    </div>
                  )}
                </ItemRow>
              ))}
            </Panel>
          )}
        </div>

        {/* Комплекты ИД */}
        <div>
          <h3 style={{ marginTop: 0 }}>Комплекты ИД (сдача в архив/СТДО)</h3>
          {can_write && (
            <Panel as="form" onSubmit={onCreatePkg}>
              <Field>
                Наименование комплекта
                <Input
                  value={pTitle}
                  onChange={(e) => setPTitle(e.target.value)}
                  placeholder="ИД по этапу 1 — фундаменты"
                />
              </Field>
              <Muted style={{ marginBottom: 6 }}>Акты ИД в комплекте:</Muted>
              {acts.length === 0 ? (
                <Muted style={{ fontSize: 12 }}>Сгенерированных актов нет (см. «Акты ИД»).</Muted>
              ) : (
                <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 8 }}>
                  {acts.map((a) => (
                    <AckOption key={a.id}>
                      <input
                        type="checkbox"
                        checked={!!pActs[a.id]}
                        onChange={(e) =>
                          setPActs((prev) => ({ ...prev, [a.id]: e.target.checked }))
                        }
                      />
                      {a.kind.toUpperCase()} {a.act_no ?? ''} · {a.work_name} ({a.status})
                    </AckOption>
                  ))}
                </div>
              )}
              <Button $variant="primary" type="submit" disabled={savingPkg || !pTitle.trim()}>
                {savingPkg ? 'Сборка…' : 'Собрать комплект'}
              </Button>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          )}

          {packages.length === 0 ? (
            <Muted>Комплектов пока нет.</Muted>
          ) : (
            <Panel>
              {packages.map((p) => (
                <ItemRow key={p.id}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 8, cursor: 'pointer' }}
                    onClick={() => setSelectedPkg(selectedPkg === p.id ? null : p.id)}
                  >
                    <span style={{ fontWeight: 600 }}>{p.title}</span>
                    <Muted style={{ fontSize: 12 }}>{pkgStatusLabel(p.status)}</Muted>
                  </div>
                  <Muted style={{ fontSize: 12, marginTop: 2 }}>
                    Актов: {p.act_ids.length}
                    {p.stdo_ref ? ` · СТДО: ${p.stdo_ref}` : ''}
                  </Muted>

                  {selectedPkg === p.id && detail && (
                    <div style={{ marginTop: 8 }}>
                      <Muted style={{ fontSize: 12, fontWeight: 600, color: detail.ready ? '#27ae60' : '#e67e22' }}>
                        Готовность: {detail.ready_count}/{detail.total}{' '}
                        {detail.ready ? '— готов к сдаче' : '— не все акты подписаны'}
                      </Muted>
                      {detail.acts.map((a) => (
                        <Muted key={a.id} style={{ fontSize: 12, marginTop: 2 }}>
                          {a.ready ? '✓' : '•'} {a.kind.toUpperCase()} {a.act_no ?? ''} — {a.work_name} ({a.status})
                        </Muted>
                      ))}
                      {can_write && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                          <Input
                            value={stdoRef}
                            onChange={(e) => setStdoRef(e.target.value)}
                            placeholder="№ в СТДО"
                            style={{ maxWidth: 120 }}
                          />
                          <Button
                            $variant="ghost"
                            $size="sm"
                            disabled={!detail.ready}
                            onClick={() =>
                              setPkgStatus({ id: p.id, status: 'submitted', stdo_ref: stdoRef || undefined })
                            }
                          >
                            Сдать
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => setPkgStatus({ id: p.id, status: 'accepted' })}
                          >
                            Принят
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => setPkgStatus({ id: p.id, status: 'rejected' })}
                          >
                            Отклонён
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </ItemRow>
              ))}
            </Panel>
          )}
        </div>
      </Columns>
    </div>
  );
}
